import { ReactNode, useId } from 'react'
import {
  Box,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from '@mui/material'

export interface DefaultTextFieldProps extends Omit<TextFieldProps, 'label'> {
  label?: ReactNode
  containerSx?: SxProps<Theme>
  labelSx?: SxProps<Theme>
  resizable?: boolean
}

export default function DefaultTextField({
  containerSx,
  error,
  fullWidth,
  helperText,
  id,
  label,
  labelSx,
  name,
  resizable,
  sx,
  ...props
}: DefaultTextFieldProps) {
  const generatedId = useId()
  const inputId = id ?? (name ? `${name}-field` : generatedId)

  return (
    <Box
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          width: fullWidth ? '100%' : undefined,
        },
        ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
      ]}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={inputId}
          variant="bodySm"
          sx={[
            (theme) => ({
              color: error
                ? theme.vars.palette.error.main
                : theme.vars.palette.neutral[900],
            }),
            ...(Array.isArray(labelSx) ? labelSx : [labelSx]),
          ]}
        >
          {label}
        </Typography>
      )}
      <TextField
        {...props}
        id={inputId}
        name={name}
        fullWidth={fullWidth}
        error={error}
        helperText={helperText}
        sx={[
          resizable
            ? {
                '& textarea': {
                  resize: 'vertical',
                },
              }
            : {},
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    </Box>
  )
}
